import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import {
  MapPin, Plus, Trash, ArrowsClockwise, PlayCircle, CheckCircle, XCircle,
  TestTube, Stack, Buildings, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BranchRouting() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pincode: 0, city: 0 });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ kind: "", search: "", branch_id: "" });

  // Add form
  const [form, setForm] = useState({ kind: "pincode", pattern: "", branch_id: "", priority: 50, active: true });
  const [adding, setAdding] = useState(false);

  // Tester
  const [test, setTest] = useState({ pincode: "", city: "", address: "" });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Backfill
  const [backfill, setBackfill] = useState({ limit: 500, only_unassigned: true });
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [applying, setApplying] = useState(false);

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([
        api.get("/branch-routing"),
        branches.length ? Promise.resolve({ data: branches }) : api.get("/branches"),
      ]);
      setRules(r.data.rules);
      setStats(r.data.stats);
      setBranches(b.data);
      if (!form.branch_id && b.data.length) {
        setForm((f) => ({ ...f, branch_id: b.data[0].id }));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filteredRules = useMemo(() => {
    let out = rules;
    if (filter.kind) out = out.filter((r) => r.kind === filter.kind);
    if (filter.branch_id) out = out.filter((r) => r.branch_id === filter.branch_id);
    if (filter.search) {
      const s = filter.search.toLowerCase();
      out = out.filter((r) => String(r.pattern).toLowerCase().includes(s));
    }
    return out;
  }, [rules, filter]);

  const addRule = async (e) => {
    e.preventDefault();
    if (!form.pattern.trim() || !form.branch_id) { toast.error("Pattern + branch are required"); return; }
    setAdding(true);
    try {
      await api.post("/branch-routing", {
        kind: form.kind, pattern: form.pattern.trim(),
        branch_id: form.branch_id, priority: parseInt(form.priority, 10) || 50,
        active: form.active,
      });
      toast.success("Routing rule added");
      setForm((f) => ({ ...f, pattern: "" }));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setAdding(false);
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/branch-routing/${r.id}`, { active: !r.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setBranchOnRule = async (r, branch_id) => {
    try {
      await api.patch(`/branch-routing/${r.id}`, { branch_id });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.kind} rule "${r.pattern}"?`)) return;
    try {
      await api.delete(`/branch-routing/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/branch-routing/test", test);
      setTestResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setTesting(false);
  };

  const runBackfill = async (dry_run) => {
    if (!dry_run && !window.confirm(`This will assign branches to up to ${backfill.limit} leads. Continue?`)) return;
    dry_run ? setBackfilling(true) : setApplying(true);
    try {
      const { data } = await api.post("/branch-routing/backfill", { ...backfill, dry_run });
      setBackfillResult(data);
      if (!dry_run) toast.success(`Applied — ${data.applied} leads routed`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBackfilling(false); setApplying(false);
  };

  if (!isAdmin) {
    return <div className="p-8 text-sm text-muted-foreground">Admins only.</div>;
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Phase 1 · Stop the bleeding"
        title="Branch routing"
        description="Auto-assign every new lead to a clinic by pincode or city — and backfill the 84% that arrived without one."
        actions={
          <button onClick={load} data-testid="routing-refresh"
            className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
            <ArrowsClockwise size={16} /> Refresh
          </button>
        }
      />

      {/* KPI strip */}
      <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="routing-stats">
        <Kpi label="Total rules" value={stats.total} icon={Stack} color="#002FA7" testid="kpi-total" />
        <Kpi label="Pincode rules" value={stats.pincode} icon={MapPin} color="#008A00" testid="kpi-pincode" />
        <Kpi label="City rules" value={stats.city} icon={Buildings} color="#FF9800" testid="kpi-city" />
        <Kpi label="Active" value={stats.active} icon={CheckCircle} color="#008A00" testid="kpi-active" />
      </div>

      {/* Test + Backfill row */}
      <div className="px-8 pt-6 grid lg:grid-cols-2 gap-6">
        {/* Tester */}
        <Section title="Test a routing" subtitle="Try a pincode, city or address — see which branch it would land on." testid="section-tester">
          <div className="bg-white border border-border p-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <LabeledInput label="Pincode" testid="test-pin" value={test.pincode} placeholder="560078"
                onChange={(v) => setTest({ ...test, pincode: v })} />
              <LabeledInput label="City" testid="test-city" value={test.city} placeholder="mysuru"
                onChange={(v) => setTest({ ...test, city: v })} />
            </div>
            <LabeledInput label="Address blob" testid="test-address" value={test.address}
              placeholder="Flat 4, JP Nagar 7, Bangalore 560078"
              onChange={(v) => setTest({ ...test, address: v })} />
            <button onClick={runTest} disabled={testing} data-testid="test-run"
              className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2 disabled:opacity-50">
              <TestTube size={14} /> {testing ? "Testing…" : "Test routing"}
            </button>
            {testResult && (
              <div className="mt-2 border border-border bg-secondary p-3" data-testid="test-result">
                {testResult.matched ? (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle size={16} weight="bold" style={{ color: "#008A00" }} />
                    <span className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: "#008A00" }}>matched</span>
                    <span>→</span>
                    <span className="font-bold">{testResult.branch?.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      pin: {testResult.pincode || "—"} · city: {testResult.city || "—"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <XCircle size={16} weight="bold" style={{ color: "#E00000" }} />
                    <span className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: "#E00000" }}>no match</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      pin: {testResult.pincode || "—"} · city: {testResult.city || "—"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>

        {/* Backfill */}
        <Section title="Backfill unrouted leads" subtitle="Dry-run first — write only after you've checked the numbers." testid="section-backfill">
          <div className="bg-white border border-border p-5 space-y-3">
            <div className="grid grid-cols-2 gap-2 items-center">
              <LabeledInput label="Limit (max 5000)" testid="backfill-limit" value={backfill.limit} type="number"
                onChange={(v) => setBackfill({ ...backfill, limit: parseInt(v, 10) || 500 })} />
              <label className="flex items-center gap-2 text-sm pt-5">
                <input type="checkbox" checked={backfill.only_unassigned}
                  onChange={(e) => setBackfill({ ...backfill, only_unassigned: e.target.checked })}
                  data-testid="backfill-only-unassigned" className="accent-primary" />
                Only unassigned
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => runBackfill(true)} disabled={backfilling || applying}
                data-testid="backfill-dry-run"
                className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 disabled:opacity-50">
                <PlayCircle size={14} /> {backfilling ? "Scanning…" : "Dry-run"}
              </button>
              <button onClick={() => runBackfill(false)}
                disabled={!backfillResult || backfillResult.matched === 0 || applying || backfilling}
                data-testid="backfill-apply"
                className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2 disabled:opacity-50">
                {applying ? "Applying…" : `Apply${backfillResult ? ` (${backfillResult.matched})` : ""}`}
              </button>
            </div>
            {backfillResult && (
              <div className="border border-border bg-secondary p-3 text-sm space-y-1" data-testid="backfill-result">
                <div className="flex gap-4">
                  <Stat lbl="Scanned" v={backfillResult.scanned} />
                  <Stat lbl="Matched" v={backfillResult.matched} c="#008A00" />
                  <Stat lbl="Unmatched" v={backfillResult.unmatched} c="#FF9800" />
                  <Stat lbl="Applied" v={backfillResult.applied} c="#002FA7" />
                </div>
                {backfillResult.proposals?.length > 0 && (
                  <details className="pt-2">
                    <summary className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground cursor-pointer hover:text-primary">
                      Show first {Math.min(backfillResult.proposals.length, 10)} proposals
                    </summary>
                    <div className="mt-2 space-y-1">
                      {backfillResult.proposals.slice(0, 10).map((p) => (
                        <div key={p.lead_id} className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-muted-foreground">{p.phone || "—"}</span>
                          <span>→</span>
                          <span className="font-bold">{branchMap[p.branch_id] || p.branch_id.slice(0, 8)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </Section>
      </div>

      {/* Add new rule */}
      <div className="px-8 pt-6">
        <h2 className="font-display text-base font-bold tracking-tight mb-3">Add routing rule</h2>
        <form onSubmit={addRule} className="bg-white border border-border p-4 flex flex-wrap gap-2 items-end" data-testid="add-rule-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Kind</div>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="add-rule-kind"
              className="text-sm border border-border px-3 py-2 rounded-sm bg-white">
              <option value="pincode">Pincode</option>
              <option value="city">City</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">
              Pattern {form.kind === "pincode" ? "(6 digits)" : "(lowercased)"}
            </div>
            <input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              placeholder={form.kind === "pincode" ? "560078" : "mysuru"}
              data-testid="add-rule-pattern"
              className="text-sm border border-border px-3 py-2 rounded-sm bg-white w-40" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Branch</div>
            <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
              data-testid="add-rule-branch"
              className="text-sm border border-border px-3 py-2 rounded-sm bg-white">
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Priority</div>
            <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              data-testid="add-rule-priority"
              className="text-sm border border-border px-3 py-2 rounded-sm bg-white w-20" />
          </div>
          <button type="submit" disabled={adding} data-testid="add-rule-submit"
            className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2 disabled:opacity-50">
            <Plus size={14} weight="bold" /> {adding ? "Adding…" : "Add rule"}
          </button>
        </form>
      </div>

      {/* Rules table */}
      <div className="px-8 pt-6 pb-8">
        <div className="flex flex-wrap gap-2 items-center mb-3" data-testid="rule-filters">
          <h2 className="font-display text-base font-bold tracking-tight mr-auto">Active rules</h2>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              placeholder="Search pattern"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              data-testid="rule-filter-search"
              className="text-sm border border-border pl-9 pr-3 py-2 rounded-sm bg-white w-56"
            />
          </div>
          <select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
            data-testid="rule-filter-kind"
            className="text-sm border border-border px-3 py-2 rounded-sm bg-white">
            <option value="">All kinds</option>
            <option value="pincode">Pincode</option>
            <option value="city">City</option>
          </select>
          <select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}
            data-testid="rule-filter-branch"
            className="text-sm border border-border px-3 py-2 rounded-sm bg-white">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <span className="text-xs tabular-nums text-muted-foreground" data-testid="rule-count">
            {filteredRules.length} of {rules.length}
          </span>
        </div>

        <div className="bg-white border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="rules-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold w-20">Kind</th>
                <th className="px-4 py-3 font-bold">Pattern</th>
                <th className="px-4 py-3 font-bold">Routes to</th>
                <th className="px-4 py-3 font-bold w-20 text-right">Priority</th>
                <th className="px-4 py-3 font-bold w-24">Active</th>
                <th className="px-4 py-3 font-bold w-16 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : filteredRules.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">No rules match these filters.</td></tr>
              ) : filteredRules.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`rule-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold"
                      style={{ color: r.kind === "pincode" ? "#008A00" : "#FF9800" }}>
                      {r.kind}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.pattern}</td>
                  <td className="px-4 py-2.5">
                    <select value={r.branch_id} onChange={(e) => setBranchOnRule(r, e.target.value)}
                      data-testid={`rule-branch-${r.id}`}
                      className="text-xs border border-border px-2 py-1 rounded-sm bg-white">
                      {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs">{r.priority}</td>
                  <td className="px-4 py-2.5">
                    <button onClick={() => toggleActive(r)} data-testid={`rule-toggle-${r.id}`}
                      className={`text-xs uppercase tracking-[0.15em] font-bold px-2 py-1 ${
                        r.active ? "text-[#008A00]" : "text-muted-foreground line-through"
                      }`}>
                      {r.active ? "Active" : "Off"}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => remove(r)} data-testid={`rule-delete-${r.id}`}
                      className="p-1.5 hover:bg-destructive/10 text-destructive border border-transparent hover:border-destructive/40">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
        {Icon && <Icon size={16} weight="bold" style={{ color }} />}
      </div>
      <div className="font-display text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <div data-testid={testid}>
      <h2 className="font-display text-base font-bold tracking-tight">{title}</h2>
      {subtitle && <div className="text-xs text-muted-foreground mt-0.5 mb-3">{subtitle}</div>}
      {children}
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground block mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
      />
    </label>
  );
}

function Stat({ lbl, v, c = "#52525B" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{lbl}</div>
      <div className="font-display text-lg font-bold tabular-nums" style={{ color: c }}>{v}</div>
    </div>
  );
}
